import { useNavigate, useRouterState } from "@tanstack/react-router";
import { Eye, EyeOff, X } from "lucide-react";
import { useDraftPreviewAllowed } from "@/lib/usePagePreview";

/**
 * Sticky banner shown on public pages when `?preview=draft` is in the URL.
 * - If the visitor is staff: shows a "draft preview active" notice with an exit button.
 * - If the visitor is NOT staff but requested preview: shows a discreet warning that
 *   the published version is being shown instead (no info leak about admin existence).
 * - Hidden inside the /admin area to avoid double-banners.
 */
export function DraftPreviewBanner() {
  const { requested, isStaff, loading } = useDraftPreviewAllowed();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  if (!requested) return null;
  if (pathname === "/admin" || pathname.startsWith("/admin/")) return null;
  if (loading) return null;

  const exitPreview = () => {
    void navigate({
      to: pathname,
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        delete next.preview;
        return next;
      },
      replace: true,
    });
  };

  if (!isStaff) {
    return (
      <div className="sticky top-0 z-[60] border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-center text-xs text-amber-900 dark:text-amber-100">
        <span className="inline-flex items-center gap-2">
          <EyeOff size={12} />
          Aperçu du brouillon réservé aux administrateurs — vous voyez la version
          publiée.
        </span>
      </div>
    );
  }

  return (
    <div className="sticky top-0 z-[60] flex items-center justify-center gap-3 border-b border-amber-500/40 bg-amber-500/15 px-4 py-2 text-xs font-medium text-amber-950 dark:text-amber-50">
      <Eye size={13} className="shrink-0" />
      <span>
        Aperçu du brouillon — cette page affiche le contenu non publié.
      </span>
      <button
        onClick={exitPreview}
        className="inline-flex items-center gap-1 rounded-full border border-amber-600/40 bg-amber-500/20 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-950 hover:bg-amber-500/30 dark:text-amber-50"
      >
        <X size={11} /> Quitter
      </button>
    </div>
  );
}
